/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, RbacService, UserService, UtilsService, ValidationService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { OrganisationModel } from '../../../shared/models';
import { OrganisationsApiService } from '../../../shared/http';
import { nameValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-organisation-settings',
    templateUrl: './organisation-settings.component.html',
    styleUrls: ['./organisation-settings.component.scss']
})

export class OrganisationSettingsComponent extends BaseComponent {
    /**
     * Account form group.
     */
    public organisationForm: FormGroup;

    /**
     * Stored organisation details for use in the view.
     */
    public organisation: OrganisationModel;

    /**
     * Stored the current Org ID for api patch.
     */
    public currentOrgID: number;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private logService: LogService,
        private organisationApiService: OrganisationsApiService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Update organisation/user details.
     */
    public updateOrganisation(): Observable<any> {
        return new Observable(observer => {
            const formValues = this.organisationForm.value;

            this.organisationApiService.update(this.currentOrgID.toString(), formValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        // API is very quick, make it look like it is doing something!
                        const timerId = setTimeout((): void => {
                            this.organisationForm.markAsPristine();
                            this.setUpPageData();
                            observer.next();
                            observer.complete();
                            clearTimeout(timerId);
                        }, 1500);

                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'OrganisationSettingsComponent: updatedAccount',
                            'Error submitting data', error
                        );
                        observer.error(error);
                        observer.complete();
                    },
                });
        });
    }

    /**
     * Setup user form.
     * @private
     */
    private setupFormGroup(): void {
        this.organisationForm = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            countryCode: [null],
            contactNumber: [null, [Validators.required, ValidationService.phoneNumberValidator]]
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.organisationForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (formValues: any): void => {
                    this.utilsService.debounce('organisationForm', () => {
                        if (this.utilsService.hasFormDataChanged(this.initialFormData, formValues)) {
                            this.organisationForm.markAsDirty();
                        } else {
                            this.organisationForm.markAsPristine();
                        }
                    });
                }
            });
    }

    /**
     * Get data from APIs.
     * @private
     */
    private setUpPageData(): void {
        const user: any = this.utilsService.duplicateObject(this.userService.getUser());
        this.currentOrgID = user.organisationId;

        this.organisationApiService.get(this.currentOrgID.toString())
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (organisationDetails: any): void => {
                    this.organisation = organisationDetails;
                    this.patchDataToForms(organisationDetails);
                    // Set initial values after patch.
                    this.initialFormData = this.organisationForm.value;
                });
    }

    /**
     * Patch API data to form.
     * @private
     */
    private patchDataToForms({ name, contactNumber, countryCode }): void {
        const organisationDetails = {};
        organisationDetails['name'] = name;

        organisationDetails['contactNumber'] = this.utilsService.cleanPhoneNumber(contactNumber, countryCode);
        if (countryCode) {
            organisationDetails['countryCode'] = countryCode;
        }

        this.organisationForm.patchValue(organisationDetails);
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'organization-settings-update': [
                { path: '/api/v1/organisations/:id', methods: ['PATCH'] }
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
